import { Component, ChangeDetectionStrategy, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxExtendedPdfViewerModule, NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';

export interface PageRenderedEvent {
  pageNumber: number;
  pageElement: HTMLElement;
  viewport: unknown;
}

@Component({
  selector: 'app-cad-pdf-viewer',
  standalone: true,
  imports: [CommonModule, NgxExtendedPdfViewerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pdf-viewer.component.html',
  host: { class: 'block h-full' },
})
export class PdfViewerComponent {
  private source = inject(PDF_SOURCE);
  private pdfService = inject(NgxExtendedPdfViewerService);
  readonly showZoomBar = input(false);
  readonly pageRendered = output<PageRenderedEvent>();
  readonly loadFailed = output<unknown>();
  readonly currentSrc = this.source.current;

  /**
   * Scrolls the viewer to a page, optionally to a vertical spot. The viewer interprets the
   * percentage against the page's scrollable extent (page height minus viewport height), so
   * the spot lands near the top of the viewport; pages shorter than the viewport scroll to
   * the page top. Percentages stay correct across zoom levels (pixel offsets would not).
   * Defensive no-op when the document is not ready: the underlying API dereferences page DOM
   * and would throw mid-load or on a stale root-singleton service.
   */
  scrollTo(page: number, topPercent: number): void {
    try {
      this.pdfService.scrollPageIntoView(page, { top: `${topPercent}%` });
    } catch {
      // Viewer not ready or page out of range; the jump is a best-effort affordance.
    }
  }

  onPageRendered(ev: {
    pageNumber: number;
    source: { div: HTMLElement; viewport: unknown };
  }): void {
    this.pageRendered.emit({
      pageNumber: ev.pageNumber,
      pageElement: ev.source.div,
      viewport: ev.source.viewport,
    });
  }
}
